import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Smartphone, Copy, Check, X, ArrowUpRight, ExternalLink, Info } from 'lucide-react';
import { type MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import '../css/zizaits-download.css'
import appicon from './zizaits_icon.png'
import screen0 from './screen0.jpg'
import screen1 from './screen1.jpg'
import screen2 from './screen2.jpg'
import screen3 from './screen3.jpg'

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.zizaits&hl=zh_TW';

/** 跳转被拦截后，多久判定为「没有跳转」（毫秒） */
const REDIRECT_CHECK_DELAY = 2500;

/** 识别会拦截下载/外链跳转的内置浏览器环境 */
function detectBlockedEnv(): 'wechat' | 'inapp' | 'normal' {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('micromessenger') || ua.includes('weixin') || ua.includes('wechat')) {
        return 'wechat';
    }
    if (
        ua.includes('weibo') ||
        ua.includes('qq/') ||
        ua.includes(' qq') ||
        ua.includes('dingtalk') ||
        ua.includes('alipayclient') ||
        ua.includes('baiduboxapp') ||
        ua.includes('quark') ||
        ua.includes('douban') ||
        ua.includes('douyin') ||
        ua.includes('toutiao')
    ) {
        return 'inapp';
    }
    return 'normal';
}

const translations = [
    {
        appName: '自在听书',
        tagline: '结合文字阅读与语音朗读的阅读工具',
        intro: '想把小说变成有声书？自在听书是一款结合文字阅读与语音朗读的阅读工具，支持导入 TXT 等文字文件，让你用自己的方式阅读和聆听喜爱的内容。无论是网络小说、长篇故事、电子书，还是自己保存的文章，都可以导入 App，享受更自在的阅读体验。',
        f1Title: '导入自己的书籍',
        f1Desc: '支持 TXT 文字文件导入；轻松阅读自己保存的小说与文章；让喜爱的文字内容集中管理。',
        f2Title: '文字朗读，阅读也能用听的',
        f2Desc: '将文字转换为语音，享受听书体验；阅读小说时，也能透过聆听放松双眼；适合通勤、休息或不方便长时间看屏幕时使用。',
        f3Title: '本机语音生成，离线也能听',
        f3Desc: '在设备本机生成朗读语音；无需依赖云端文字转语音服务；生成后可在本机播放，方便离线聆听。',
        f4Title: '专注于自己的阅读体验',
        f4Desc: '适合喜欢 TXT 小说与长篇文字的使用者；不必依赖单一小说平台的阅读界面；让阅读与聆听更符合自己的生活节奏。',
        screenshotTitle: '应用图示',
        playBtn: 'Google Play 下载',
        playSub: 'Android',
        home: '首页',
        privacy: '隐私政策',
        term: '使用条款',
        copyright: 'Copyright © 2026 广州野离子网络科技有限公司. All rights reserved.',
        footerTip: '自在听书由广州野离子网络科技有限公司研发',
        wxTip: '当前在微信中打开，下载链接可能被限制',
        guideWxTitle: '请点击右上角，在浏览器中打开',
        guideWxDesc: '微信内置浏览器不支持直接跳转应用商店，请按以下步骤操作：',
        guideWxStep1: '点击右上角的「···」按钮',
        guideWxStep2: '选择「在浏览器打开」',
        guideWxStep3: '在浏览器中再次点击下载即可',
        guideWxHint: '也可以复制链接后，粘贴到系统浏览器（Chrome）中打开',
        guideFallbackTitle: '下载没有自动开始？',
        guideFallbackDesc: '当前浏览器可能限制了跳转，建议复制链接后在系统浏览器（Chrome）中打开。',
        guideFallbackHint: '如果已经开始跳转，直接关闭本提示即可。',
        guideInappTitle: '请在浏览器中打开',
        guideInappDesc: '当前应用内置浏览器可能拦截了跳转，请点击右上角菜单选择「用浏览器打开」，或复制链接后到浏览器中下载。',
        copyLink: '复制链接地址',
        copied: '已复制',
        copyFail: '复制失败，请长按链接手动复制',
        retryOpen: '继续尝试打开',
        close: '关闭',
    },
    {
        appName: '自在聽書',
        tagline: '結合文字閱讀與語音朗讀的閱讀工具',
        intro: '想把小說變成有聲書？自在聽書是一款結合文字閱讀與語音朗讀的閱讀工具，支援匯入 TXT 等文字檔案，讓你用自己的方式閱讀和聆聽喜愛的內容。無論是網路小說、長篇故事、電子書，還是自己保存的文章，都可以匯入 App，享受更自在的閱讀體驗。',
        f1Title: '匯入自己的書籍',
        f1Desc: '支援 TXT 文字檔案匯入；輕鬆閱讀自己保存的小說與文章；讓喜愛的文字內容集中管理。',
        f2Title: '文字朗讀，閱讀也能用聽的',
        f2Desc: '將文字轉換為語音，享受聽書體驗；閱讀小說時，也能透過聆聽放鬆雙眼；適合通勤、休息或不方便長時間看螢幕時使用。',
        f3Title: '本機語音生成，離線也能聽',
        f3Desc: '在裝置本機生成朗讀語音；無需依賴雲端文字轉語音服務；生成後可在本機播放，方便離線聆聽。',
        f4Title: '專注於自己的閱讀體驗',
        f4Desc: '適合喜歡 TXT 小說與長篇文字的使用者；不必依賴單一小說平台的閱讀介面；讓閱讀與聆聽更符合自己的生活節奏。',
        screenshotTitle: '應用圖示',
        playBtn: 'Google Play 下載',
        playSub: 'Android',
        home: '首頁',
        privacy: '隱私權政策',
        term: '使用條款',
        copyright: 'Copyright © 2026 廣州野離子網絡科技有限公司. All rights reserved.',
        footerTip: '自在聽書由廣州野離子網絡科技有限公司研發',
        wxTip: '當前在微信中打開，下載連結可能被限制',
        guideWxTitle: '請點擊右上角，在瀏覽器中打開',
        guideWxDesc: '微信內建瀏覽器不支援直接跳轉應用商店，請按以下步驟操作：',
        guideWxStep1: '點擊右上角的「···」按鈕',
        guideWxStep2: '選擇「在瀏覽器打開」',
        guideWxStep3: '在瀏覽器中再次點擊下載即可',
        guideWxHint: '也可以複製連結後，粘貼到系統瀏覽器（Chrome）中打開',
        guideFallbackTitle: '下載沒有自動開始？',
        guideFallbackDesc: '當前瀏覽器可能限制了跳轉，建議複製連結後在系統瀏覽器（Chrome）中打開。',
        guideFallbackHint: '如果已經開始跳轉，直接關閉本提示即可。',
        guideInappTitle: '請在瀏覽器中打開',
        guideInappDesc: '當前應用內建瀏覽器可能攔截了跳轉，請點擊右上角選單選擇「用瀏覽器打開」，或複製連結後到瀏覽器中下載。',
        copyLink: '複製連結位址',
        copied: '已複製',
        copyFail: '複製失敗，請長按連結手動複製',
        retryOpen: '繼續嘗試打開',
        close: '關閉',
    },
    {
        appName: 'ZIZAITS',
        tagline: 'A reader that reads aloud',
        intro: 'Turn your novels into audiobooks? ZIZAITS is a reading tool that combines text reading with speech playback. Import TXT and other text files and enjoy your favorite content your own way — read it or listen to it. Whether it is web novels, long stories, e-books, or articles you saved yourself, just import them into the App and enjoy a more relaxed reading experience.',
        f1Title: 'Import your own books',
        f1Desc: 'Import TXT text files; easily read your saved novels and articles; keep your favorite text content all in one place.',
        f2Title: 'Text-to-speech, read by listening',
        f2Desc: 'Convert text into speech and enjoy audiobooks; rest your eyes while listening to novels; great for commuting, breaks, or whenever reading a screen for long is inconvenient.',
        f3Title: 'On-device voice, offline listening',
        f3Desc: 'Generate narration voice right on your device; no reliance on cloud text-to-speech services; play locally after generation — listen offline.',
        f4Title: 'Focus on your own reading',
        f4Desc: 'For those who love TXT novels and long-form text; no need to rely on a single novel platform\'s reader; let reading and listening fit your own rhythm.',
        screenshotTitle: 'App Icon',
        playBtn: 'Get it on Google Play',
        playSub: 'Android',
        home: 'Home',
        privacy: 'Privacy Policy',
        term: 'Terms of Use',
        copyright: 'Copyright © 2026 Guangzhou Yelizi Network Technology Co., Ltd. All rights reserved.',
        footerTip: 'ZIZAITS is developed by Guangzhou Yelizi Network Technology Co., Ltd.',
        wxTip: 'Opened in WeChat — the store link may be blocked',
        guideWxTitle: 'Tap the top-right menu to open in a browser',
        guideWxDesc: 'WeChat in-app browser cannot jump to the store directly. Please follow these steps:',
        guideWxStep1: 'Tap the "···" button in the top-right corner',
        guideWxStep2: 'Choose "Open in Browser"',
        guideWxStep3: 'Tap download again in the browser',
        guideWxHint: 'You can also copy the link and paste it into Chrome',
        guideFallbackTitle: 'Download did not start?',
        guideFallbackDesc: 'Your browser may have blocked the redirect. Copy the link and open it in Chrome.',
        guideFallbackHint: 'If the redirect already started, just close this message.',
        guideInappTitle: 'Please open in a browser',
        guideInappDesc: 'This in-app browser may have blocked the redirect. Open the top-right menu and choose "Open in Browser", or copy the link and open it in your browser.',
        copyLink: 'Copy link',
        copied: 'Copied',
        copyFail: 'Copy failed, please long-press the link to copy manually',
        retryOpen: 'Try again',
        close: 'Close',
    }
];

function Download() {
    const [searchParams] = useSearchParams();
    const theme = searchParams.get('theme');
    const language = searchParams.get('language') || '繁体';

    const newTheme = theme === 'dark' ? 'dark' : 'light';
    document.body.className = newTheme;

    const t = language === '简体' ? translations[0] : (language === '繁体' ? translations[1] : translations[2]);
    const query = `?language=${language}&theme=${theme}`;

    /** 当前所处浏览器环境：微信 / 其它 App 内置浏览器 / 普通浏览器 */
    const env = useMemo(() => detectBlockedEnv(), []);

    /** 引导弹层：wechat=微信蒙层引导，inapp=其它内置浏览器，fallback=普通浏览器未跳转 */
    const [guide, setGuide] = useState<{ type: 'wechat' | 'inapp' | 'fallback'; url: string } | null>(null);
    const [copyState, setCopyState] = useState<'idle' | 'ok' | 'fail'>('idle');
    const timerRef = useRef<number | null>(null);

    useEffect(() => () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
    }, []);

    /* 弹层打开时锁定页面滚动，并支持 ESC 关闭 */
    useEffect(() => {
        if (!guide) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setGuide(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [guide]);

    /* 复制链接（微信等环境下 Clipboard API 不可用，降级 execCommand） */
    const copyLink = async (url: string) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                const input = document.createElement('textarea');
                input.value = url;
                input.setAttribute('readonly', '');
                input.style.position = 'fixed';
                input.style.top = '-1000px';
                input.style.opacity = '0';
                document.body.appendChild(input);
                input.select();
                input.setSelectionRange(0, url.length);
                const ok = document.execCommand('copy');
                document.body.removeChild(input);
                if (!ok) throw new Error('copy failed');
            }
            setCopyState('ok');
        } catch (e) {
            setCopyState('fail');
        }
        window.setTimeout(() => setCopyState('idle'), 2500);
    };

    /* 点击下载：微信/内置浏览器直接引导；普通浏览器先跳转，未跳转再引导 */
    const handleDownload = (e: ReactMouseEvent<HTMLAnchorElement>, url: string) => {
        if (env === 'wechat' || env === 'inapp') {
            e.preventDefault();
            setGuide({ type: env, url });
            return;
        }
        e.preventDefault();
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
            // 弹出被拦截：直接在当前页跳转；若仍停留在当前页，再提示引导
            window.location.href = url;
            if (timerRef.current) window.clearTimeout(timerRef.current);
            timerRef.current = window.setTimeout(() => {
                if (!document.hidden && document.hasFocus()) {
                    setGuide({ type: 'fallback', url });
                }
            }, REDIRECT_CHECK_DELAY);
        }
    };

    const isWechatGuide = guide?.type === 'wechat';

    return (
        <div className="zz-dl">
            <Helmet>
                <title>{t.appName} - {t.tagline}</title>
                <meta property="og:title" content={`${t.appName} - ${t.tagline}`} />
                <meta property="og:description" content={t.intro} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={`${window.location.origin}/zizaits_screen0.jpg`} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${t.appName} - ${t.tagline}`} />
                <meta name="twitter:description" content={t.intro} />
                <meta name="twitter:image" content={`${window.location.origin}/zizaits_screen0.jpg`} />
            </Helmet>

            {/* Hero */}
            <header className="zz-dl-hero">
                <img className="zz-dl-icon" src={appicon} alt={t.appName} />
                <h1 className="zz-dl-name">{t.appName}</h1>
                <p className="zz-dl-tagline">{t.tagline}</p>
                <p className="zz-dl-intro">{t.intro}</p>
                <div className="zz-dl-buttons">
                    <a
                        className="zz-dl-btn zz-dl-btn-play"
                        href={PLAY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => handleDownload(e, PLAY_URL)}
                    >
                        <Smartphone size={22} />
                        <span>
                            {t.playBtn}
                            <em className="zz-dl-btn-sub">{t.playSub}</em>
                        </span>
                    </a>
                </div>
                {env === 'wechat' && (
                    <p className="zz-dl-wxtip">
                        <Info size={14} />
                        {t.wxTip}
                    </p>
                )}
            </header>

            {/* 内容 */}
            <main className="zz-dl-body">
                <section className="zz-dl-section">
                    <div className="zz-dl-feature">
                        <h3>{t.f1Title}</h3>
                        <p>{t.f1Desc}</p>
                    </div>
                    <div className="zz-dl-feature">
                        <h3>{t.f2Title}</h3>
                        <p>{t.f2Desc}</p>
                    </div>
                    <div className="zz-dl-feature">
                        <h3>{t.f3Title}</h3>
                        <p>{t.f3Desc}</p>
                    </div>
                    <div className="zz-dl-feature">
                        <h3>{t.f4Title}</h3>
                        <p>{t.f4Desc}</p>
                    </div>
                </section>

                <section className="zz-dl-section">
                    <h2 className="zz-dl-title">{t.screenshotTitle}</h2>
                    <div className="zz-dl-shots">
                        <div className="zz-dl-shot"><img src={screen0} alt={`${t.appName} ${t.screenshotTitle} 1`} loading="lazy" /></div>
                        <div className="zz-dl-shot"><img src={screen1} alt={`${t.appName} ${t.screenshotTitle} 2`} loading="lazy" /></div>
                        <div className="zz-dl-shot"><img src={screen2} alt={`${t.appName} ${t.screenshotTitle} 3`} loading="lazy" /></div>
                        <div className="zz-dl-shot"><img src={screen3} alt={`${t.appName} ${t.screenshotTitle} 4`} loading="lazy" /></div>
                    </div>
                </section>
            </main>

            {/* 底部 */}
            <footer className="zz-dl-footer">
                <div className="zz-dl-links">
                    <Link to={`/zizaits/download${query}`}>{t.home}</Link>
                    <Link to={`/zizaits/privacyios${query}`}>{t.privacy}</Link>
                    <Link to={`/zizaits/term${query}`}>{t.term}</Link>
                </div>
                <p className="zz-dl-copy">{t.footerTip}</p>
                <p className="zz-dl-copy">{t.copyright}</p>
            </footer>

            {/* 微信内置浏览器：右上角引导蒙层 */}
            {isWechatGuide && guide && (
                <div className="zz-guide zz-guide-wechat" onClick={() => setGuide(null)}>
                    <div className="zz-guide-arrow">
                        <ArrowUpRight size={40} strokeWidth={2.5} />
                        <span>{t.guideWxStep1}</span>
                    </div>
                    <div className="zz-guide-card" onClick={(e) => e.stopPropagation()}>
                        <h3 className="zz-guide-title">{t.guideWxTitle}</h3>
                        <p className="zz-guide-desc">{t.guideWxDesc}</p>
                        <ol className="zz-guide-steps">
                            <li>{t.guideWxStep1}</li>
                            <li>{t.guideWxStep2}</li>
                            <li>{t.guideWxStep3}</li>
                        </ol>
                        <input
                            className="zz-guide-url"
                            readOnly
                            value={guide.url}
                            onFocus={(e) => e.currentTarget.select()}
                            onClick={(e) => e.currentTarget.select()}
                        />
                        <button className="zz-guide-btn" onClick={() => copyLink(guide.url)}>
                            {copyState === 'ok' ? <Check size={18} /> : <Copy size={18} />}
                            {copyState === 'ok' ? t.copied : t.copyLink}
                        </button>
                        <p className="zz-guide-hint">
                            {copyState === 'fail' ? t.copyFail : t.guideWxHint}
                        </p>
                    </div>
                </div>
            )}

            {/* 其它 App 内置浏览器 / 普通浏览器未跳转：底部弹窗引导 */}
            {guide && guide.type !== 'wechat' && (
                <div className="zz-guide zz-guide-sheet" onClick={() => setGuide(null)}>
                    <div className="zz-guide-panel" onClick={(e) => e.stopPropagation()}>
                        <span className="zz-guide-bar" />
                        <button className="zz-guide-close" onClick={() => setGuide(null)} aria-label={t.close}>
                            <X size={20} />
                        </button>
                        <h3 className="zz-guide-title">
                            {guide.type === 'inapp' ? t.guideInappTitle : t.guideFallbackTitle}
                        </h3>
                        <p className="zz-guide-desc">
                            {guide.type === 'inapp' ? t.guideInappDesc : t.guideFallbackDesc}
                        </p>
                        <input
                            className="zz-guide-url"
                            readOnly
                            value={guide.url}
                            onFocus={(e) => e.currentTarget.select()}
                            onClick={(e) => e.currentTarget.select()}
                        />
                        <div className="zz-guide-actions">
                            <button className="zz-guide-btn" onClick={() => copyLink(guide.url)}>
                                {copyState === 'ok' ? <Check size={18} /> : <Copy size={18} />}
                                {copyState === 'ok' ? t.copied : t.copyLink}
                            </button>
                            <a
                                className="zz-guide-btn zz-guide-btn-ghost"
                                href={guide.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setGuide(null)}
                            >
                                <ExternalLink size={18} />
                                {t.retryOpen}
                            </a>
                        </div>
                        <p className="zz-guide-hint">
                            {copyState === 'fail' ? t.copyFail : t.guideFallbackHint}
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Download;
